import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../dash.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft,
  faCircleInfo,
  faMobileScreenButton,
  faRotate,
  faWallet,
} from '@fortawesome/free-solid-svg-icons';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import { paybillApi } from '../../services/paybillApi';

const formatKes = (value) => `KES ${Number(value || 0).toLocaleString('en-KE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const formatDate = (value) => {
  if (!value) return '—';
  const darajaTimestamp = String(value).trim();
  if (/^\d{14}$/.test(darajaTimestamp)) {
    return `${darajaTimestamp.slice(0, 4)}-${darajaTimestamp.slice(4, 6)}-${darajaTimestamp.slice(6, 8)} ${darajaTimestamp.slice(8, 10)}:${darajaTimestamp.slice(10, 12)}:${darajaTimestamp.slice(12, 14)}`;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('en-KE');
};

function PaybillBalance() {
  const [activeTab, setActiveTab] = useState('withdraw');
  const [balance, setBalance] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshingBalance, setRefreshingBalance] = useState(false);
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const refreshData = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const [latestBalance, recipientResult] = await Promise.all([
        paybillApi('/api/balance'),
        paybillApi('/api/allowed-recipients'),
      ]);
      setBalance(latestBalance);
      const approvedNumbers = recipientResult.recipients || [];
      setRecipients(approvedNumbers);
      setSelectedRecipient((current) => current || approvedNumbers[0] || '');
    } catch (requestError) {
      setError(requestError.message || 'Could not load paybill data.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const accountTotals = useMemo(() => (balance?.accounts || []).reduce((totals, account) => ({
    balance: totals.balance + Number(account.balance || 0),
    available: totals.available + Number(account.availableBalance || 0),
  }), { balance: 0, available: 0 }), [balance]);

  const requestBalance = async () => {
    setRefreshingBalance(true);
    setError('');
    setNotice('Balance enquiry sent. Waiting for M-Pesa to reply…');
    try {
      const request = await paybillApi('/api/balance', { method: 'POST', body: {} });
      setBalance((current) => ({ ...(current || {}), status: 'pending', requestId: request.requestId }));

      for (let attempt = 0; attempt < 20; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 3000));
        const latest = await paybillApi('/api/balance');
        setBalance(latest);
        if (latest.requestId === request.requestId && latest.status !== 'pending') {
          setNotice(latest.status === 'complete'
            ? 'Latest paybill balance received.'
            : latest.resultDescription || 'M-Pesa returned an unsuccessful balance enquiry.');
          return;
        }
      }
      setNotice('The request is still pending. Refresh the balance in a moment to check for the callback.');
      await refreshData({ quiet: true });
    } catch (requestError) {
      setError(requestError.message || 'Could not request the paybill balance.');
      setNotice('');
    } finally {
      setRefreshingBalance(false);
    }
  };

  const submitWithdrawal = async (event) => {
    event.preventDefault();
    const numericAmount = Number(amount);
    if (!selectedRecipient) {
      setError('There are no approved withdrawal numbers configured.');
      return;
    }
    if (!Number.isInteger(numericAmount) || numericAmount < 10 || numericAmount > 250000) {
      setError('Enter a whole KES amount from 10 to 250,000.');
      return;
    }
    if (!window.confirm(`Send ${formatKes(numericAmount)} to ${selectedRecipient}? This submits a live M-Pesa withdrawal.`)) {
      return;
    }

    setSubmittingWithdrawal(true);
    setError('');
    setNotice('Submitting the withdrawal to M-Pesa…');
    try {
      const result = await paybillApi('/api/withdrawals', {
        method: 'POST',
        body: { phoneNumber: selectedRecipient, amount: numericAmount },
      });
      setAmount('');
      setNotice(`${result.message || 'Withdrawal accepted.'} Request ID: ${result.requestId || 'pending'}`);
      await refreshData({ quiet: true });
    } catch (requestError) {
      setError(requestError.message || 'The withdrawal could not be submitted.');
      setNotice('');
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  const connectionLabel = error
    ? 'API unavailable'
    : loading
      ? 'Connecting…'
      : 'API connected';

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />
        <main className="clientsComp paybillPage">
          <header className="paybillHeader">
            <div>
              <p className="paybillEyebrow">Business Info <span>/</span> M-Pesa</p>
              <h1>Paybill balance</h1>
              <p className="paybillHeaderText">Request the live balance and send approved M-Pesa payouts.</p>
            </div>
            <div className={`paybillConnectionState ${error ? 'is-error' : 'is-connected'}`}>
              <span className="paybillStateDot" />
              {connectionLabel}
            </div>
          </header>

          {error && <div className="paybillAlert paybillAlertError" role="alert">{error}</div>}
          {notice && <div className="paybillAlert" role="status">{notice}</div>}

          <section className="paybillBalanceGrid" aria-label="Paybill balances">
            <article className="paybillBalanceCard paybillBalanceCardPrimary">
              <div className="paybillBalanceCardTop">
                <span className="paybillBalanceIcon"><FontAwesomeIcon icon={faWallet} /></span>
                <span className="paybillBalanceTag">Paybill account</span>
              </div>
              <p>Current paybill balance</p>
              <strong>{balance?.status === 'complete' && balance.accounts?.length
                ? formatKes(accountTotals.balance)
                : <span>{balance?.status === 'pending' ? 'Pending…' : '— — —'}</span>}</strong>
              <small>{balance?.status === 'complete'
                ? `Last response ${formatDate(balance.completedAt || balance.requestedAt)}`
                : balance?.resultDescription || 'Request a fresh balance from M-Pesa.'}</small>
            </article>
            <article className="paybillBalanceCard">
              <div className="paybillBalanceCardTop">
                <span className="paybillBalanceIcon paybillBalanceIconGreen"><FontAwesomeIcon icon={faArrowRightArrowLeft} /></span>
                <span className="paybillBalanceTag">Available to withdraw</span>
              </div>
              <p>Available balance</p>
              <strong>{balance?.status === 'complete' && balance.accounts?.length
                ? formatKes(accountTotals.available)
                : <span>— — —</span>}</strong>
              <small>M-Pesa returns this amount with each completed balance enquiry.</small>
            </article>
            <article className="paybillBalanceNote paybillBalanceActions">
              <span className="paybillNoteIcon"><FontAwesomeIcon icon={faCircleInfo} /></span>
              <div>
                <strong>Secure paybill access</strong>
                <p>Requests use your signed-in, OTP-verified admin account.</p>
                <button type="button" className="paybillActionButton" onClick={requestBalance} disabled={refreshingBalance || loading}>
                  <FontAwesomeIcon icon={faRotate} spin={refreshingBalance} />
                  {refreshingBalance ? 'Checking…' : 'Check balance'}
                </button>
              </div>
            </article>
          </section>

          <nav className="paybillTabs" aria-label="Paybill sections">
            <button type="button" className={activeTab === 'withdraw' ? 'is-active' : ''} onClick={() => setActiveTab('withdraw')}>
              <FontAwesomeIcon icon={faArrowRightArrowLeft} /> Withdraw
            </button>
            <button type="button" className={activeTab === 'accounts' ? 'is-active' : ''} onClick={() => setActiveTab('accounts')}>
              <FontAwesomeIcon icon={faWallet} /> Accounts
            </button>
          </nav>

          {activeTab === 'withdraw' ? (
            <section className="paybillWorkGrid">
            <form className="paybillPanel paybillWithdrawalPanel" onSubmit={submitWithdrawal}>
              <div className="paybillPanelHeader">
                <div>
                  <h2>New withdrawal</h2>
                  <p>Send an M-Pesa payout to an approved recipient.</p>
                </div>
                <span className="paybillComingBadge">KES 10–250,000</span>
              </div>

              <label className="paybillFieldLabel" htmlFor="payout-destination">Send to</label>
              <div className="paybillDestinationField">
                <span className="paybillPhoneIcon"><FontAwesomeIcon icon={faMobileScreenButton} /></span>
                {recipients.length ? (
                  <select id="payout-destination" value={selectedRecipient} onChange={(event) => setSelectedRecipient(event.target.value)}>
                    {recipients.map((recipient) => <option key={recipient} value={recipient}>{recipient}</option>)}
                  </select>
                ) : (
                  <span className="paybillNoRecipients">No approved payout numbers are configured.</span>
                )}
              </div>

              <label className="paybillFieldLabel paybillAmountLabel" htmlFor="withdrawal-amount">Amount</label>
              <div className="paybillAmountInput">
                <span>KES</span>
                <input
                  id="withdrawal-amount"
                  type="number"
                  inputMode="numeric"
                  min="10"
                  max="250000"
                  step="1"
                  placeholder="0"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  disabled={!recipients.length || submittingWithdrawal}
                  required
                />
              </div>
              <div className="paybillAmountMeta">
                <span>Available balance</span>
                <strong>{balance?.status === 'complete' ? formatKes(accountTotals.available) : 'Not checked'}</strong>
              </div>

              <button type="submit" className="paybillWithdrawButton is-enabled" disabled={!recipients.length || submittingWithdrawal || loading}>
                {submittingWithdrawal ? 'Submitting withdrawal…' : `Send ${amount ? formatKes(amount) : 'withdrawal'}`}
              </button>
              <p className="paybillSecureNote"><FontAwesomeIcon icon={faCircleInfo} /> M-Pesa processes withdrawals asynchronously. Keep the request ID shown after submission for follow-up.</p>
            </form>

            <aside className="paybillPanel paybillNumbersPanel">
              <div className="paybillPanelHeader">
                <div>
                  <h2>Approved M-Pesa numbers</h2>
                  <p>Only server-approved destinations can receive withdrawals.</p>
                </div>
                <span className="paybillCountBadge">{recipients.length}</span>
              </div>
              {recipients.length ? (
                <ul className="paybillRecipientList">
                  {recipients.map((recipient) => <li key={recipient}><FontAwesomeIcon icon={faMobileScreenButton} /> {recipient}</li>)}
                </ul>
              ) : (
                <div className="paybillNumbersEmpty">
                  <span className="paybillEmptyIcon"><FontAwesomeIcon icon={faMobileScreenButton} /></span>
                  <h3>No numbers configured</h3>
                  <p>Ask a system administrator to update the server-side withdrawal allowlist.</p>
                </div>
              )}
              <div className="paybillSecurityFootnote">
                <FontAwesomeIcon icon={faCircleInfo} />
                <span>The API validates both the recipient and amount before sending a payout.</span>
              </div>
            </aside>
            </section>
          ) : (
            <section className="paybillPanel paybillAccountsPanel" aria-label="Paybill account breakdown">
              <div className="paybillPanelHeader">
                <div>
                  <h2>Paybill account balances</h2>
                  <p>Balance and availability by M-Pesa account.</p>
                </div>
                <span className="paybillCountBadge">{balance?.accounts?.length || 0}</span>
              </div>
              {balance?.accounts?.length ? (
                <div className="paybillAccountTableWrap">
                  <table className="paybillAccountTable">
                    <thead>
                      <tr><th>Account</th><th>Balance</th><th>Available</th><th>Uncleared</th><th>Reserved</th></tr>
                    </thead>
                    <tbody>
                      {balance.accounts.map((account) => (
                        <tr key={`${account.name}-${account.currency}`}>
                          <td>{account.name}</td>
                          <td>{formatKes(account.balance)}</td>
                          <td>{formatKes(account.availableBalance)}</td>
                          <td>{formatKes(account.unclearedBalance)}</td>
                          <td>{formatKes(account.reservedBalance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="paybillAccountEmpty">
                  <p>{loading ? 'Loading account balances…' : 'Request a balance to see the account breakdown.'}</p>
                  <button type="button" className="paybillActionButton" onClick={requestBalance} disabled={refreshingBalance || loading}>
                    <FontAwesomeIcon icon={faRotate} spin={refreshingBalance} />
                    {refreshingBalance ? 'Checking…' : 'Check balance'}
                  </button>
                </div>
              )}
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

export default PaybillBalance;
